(function () {
  'use strict';

  /*
    Clean view toggle.

    All the hiding is assets/bc-clean-view.css, keyed off one class on
    <body>. This file only decides when that class is on, remembers it for
    the session, and keeps the button's label honest.

    Nothing here touches a widget. Three of the four are third-party embeds
    and Judge.me re-renders its own tab after load; a class on an ancestor
    keeps applying to whatever they inject, whenever they inject it, which
    is precisely what scripting each node could not promise.
  */

  var BODY_CLASS = 'bc-clean-view';
  var STORAGE_KEY = 'bc-clean-view';
  var MOBILE_QUERY = '(max-width: 749px)';

  if (window.__bcCleanViewBound) return;
  window.__bcCleanViewBound = true;

  function readStored() {
    try {
      return window.sessionStorage.getItem(STORAGE_KEY) === '1';
    } catch (error) {
      // Private mode, or storage disabled: the toggle still works, it just
      // does not survive the next page.
      return false;
    }
  }

  function writeStored(on) {
    try {
      if (on) window.sessionStorage.setItem(STORAGE_KEY, '1');
      else window.sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      /* not fatal - see readStored */
    }
  }

  /*
    Where the button sits on a phone.

    It is the bottom segment of the right-edge column the Judge.me reviews
    spine forms, and the two are separate fixed elements: the spine is
    injected by Judge.me and is not ours to wrap. So the stylesheet needs
    the spine's height as a number, and the number is not a constant - the
    label is "Judge.me * <rating> * <n> reviews" and grows with the review
    count.

    Measured here into --bc-jdgm-spine-h, with a flag on <html> while the
    spine is actually on screen. Nothing about this is scroll-driven: it is
    a layout number, read when the spine appears, when it resizes, when the
    viewport changes and when clean view toggles it away.

    The width goes with it. Left to size itself the button came out 32px
    against the spine's 29 - a 12px label and a 15px icon against their
    13px label - and 3px is a visible step in a column whose whole point is
    to read as one block. Taking their width means it stays flush whatever
    Judge.me does to their own padding.
  */
  var SPINE_SELECTOR = '.jdgm-revs-tab-btn';
  var SPINE_FLAG = 'bc-has-jdgm-spine';
  var spineSize = null;
  var spineWatch = null;

  function syncSpine() {
    var spine = document.querySelector(SPINE_SELECTOR);
    var height = 0;
    var width = 0;

    if (spine) {
      // display:none measures 0 anyway; visibility does not, and clean view
      // could plausibly be re-implemented with either.
      var styles = window.getComputedStyle ? window.getComputedStyle(spine) : null;
      if (!styles || (styles.display !== 'none' && styles.visibility !== 'hidden')) {
        var rect = spine.getBoundingClientRect();
        height = Math.round(rect.height);
        width = Math.round(rect.width);
      }

      if (!spineSize && window.ResizeObserver) {
        spineSize = new ResizeObserver(syncSpine);
        spineSize.observe(spine);
      }
    }

    var root = document.documentElement;
    root.style.setProperty('--bc-jdgm-spine-h', height + 'px');
    root.style.setProperty('--bc-jdgm-spine-w', width + 'px');
    if (root.classList) root.classList.toggle(SPINE_FLAG, height > 0);
    return height > 0;
  }

  /*
    Judge.me renders its tab after this script runs, so the first
    measurement usually finds nothing. Watch until it lands, then stop -
    the ResizeObserver above carries it from there. The timeout is the
    backstop for a shop with the tab switched off in the Judge.me admin,
    where the element never arrives and there is nothing to wait for.
  */
  function watchForSpine() {
    if (syncSpine()) return;
    if (!window.MutationObserver) return;

    spineWatch = new MutationObserver(function () {
      if (syncSpine() && spineWatch) {
        spineWatch.disconnect();
        spineWatch = null;
      }
    });
    spineWatch.observe(document.body, { childList: true, subtree: true });

    window.setTimeout(function () {
      if (spineWatch) {
        spineWatch.disconnect();
        spineWatch = null;
      }
    }, 15000);
  }

  function init() {
    var toggle = document.querySelector('[data-bc-clean-view-toggle]');
    if (!toggle) return;

    var label = toggle.querySelector('[data-bc-clean-view-label]');
    var mobile = window.matchMedia ? window.matchMedia(MOBILE_QUERY) : null;
    var hideText = toggle.getAttribute('data-label-hide') || 'Hide';
    var showText = toggle.getAttribute('data-label-show') || 'Show';
    var hideAria = toggle.getAttribute('data-aria-hide') || hideText;
    var showAria = toggle.getAttribute('data-aria-show') || showText;

    function paint(on) {
      document.body.classList.toggle(BODY_CLASS, on);
      if (label) label.textContent = on ? showText : hideText;
      toggle.setAttribute('aria-pressed', on ? 'true' : 'false');
      toggle.setAttribute('aria-label', on ? showAria : hideAria);
      // Clean view takes the spine down with everything else, so the
      // button's own anchor changes with it - now, not one observer tick
      // later.
      syncSpine();
    }

    function apply() {
      // Above the mobile breakpoint the button is display:none, so a class
      // left on from a phone-width session would hide four widgets with no
      // way to bring them back. The stored preference is kept - rotating
      // back to a narrow width restores it.
      var wanted = readStored() && (!mobile || mobile.matches);
      paint(wanted);
    }

    toggle.addEventListener('click', function () {
      var next = !document.body.classList.contains(BODY_CLASS);
      writeStored(next);
      paint(next);
    });

    if (mobile) {
      if (mobile.addEventListener) mobile.addEventListener('change', apply);
      else if (mobile.addListener) mobile.addListener(apply);
    }

    window.addEventListener('resize', syncSpine);
    window.addEventListener('orientationchange', syncSpine);
    window.addEventListener('load', syncSpine);

    apply();
    watchForSpine();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
